import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import classnames from 'classnames';
import React, { useEffect, useId, useState } from 'react';

import { IconDown16 } from '../icons/ic-down';
import ErrorLabel from '../label/ErrorLabel';
import InputLabel from '../label/InputLabel';

import { DROPDOWN_SCROLLBAR_CLASSNAME } from './dropdown-scrollbar';

export type BasicDropdownOption<TValue = string | number> = {
    value: TValue;
    label?: string | React.JSX.Element;
    isSelectable?: boolean;
};

export enum BasicDropdownStyle {
    default = 'default',
    light = 'light',
    dropdown_on_white_bg = 'dropdown_on_white_bg',
    bg_white = 'bg_white'
}

export type BasicDropdownStyleConfig = {
    itemBgColorClass?: string;
    listBgColorClass?: string;
    selectedItemClassName?: string;
    style?: BasicDropdownStyle;
};

export type BasicDropdownProps<TValue = string | number> = {
    className?: string;
    itemClassName?: string;
    /** Extra classes for the trigger button (e.g. `w-full` to make it fill the container instead of fitting the selected option). */
    buttonClassName?: string;
    style?: BasicDropdownStyle | BasicDropdownStyleConfig;
    height?: 'default-48' | 'custom';
    label?: string;
    placeholder?: string | React.JSX.Element;
    preselectedIndex?: number;
    options: BasicDropdownOption<TValue>[];
    onChange?: (selectedItem: BasicDropdownOption<TValue>, index: number) => boolean | void;
    onClose?: () => void;
    customIcon?: React.JSX.Element;
    disabled?: boolean;
    isDropdownAbsolutePositioned?: boolean;
    error?: string;
    openingDirection?: 'up' | 'down';
    required?: boolean;
    paginationRef?: (node?: Element) => void;
};

/**
 * Helper component to observe Listbox open state and fire onClose.
 * Replicates the original useEffect([isDropdownOpen]) behavior.
 */
function CloseObserver({ open, onClose }: { open: boolean; onClose?: () => void }) {
    useEffect(() => {
        if (!open && onClose) {
            onClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    return null;
}

function BasicDropdown<TValue>(props: BasicDropdownProps<TValue>): React.JSX.Element {
    const {
        className,
        itemClassName,
        buttonClassName,
        style,
        height,
        label,
        preselectedIndex,
        options,
        onChange,
        onClose,
        customIcon,
        disabled,
        isDropdownAbsolutePositioned,
        placeholder,
        error,
        openingDirection,
        paginationRef
    } = props;

    const [selectedOption, setSelectedOption] = useState<number>(
        placeholder && preselectedIndex === undefined ? -1 : preselectedIndex ? preselectedIndex : 0
    );
    const [focused, setFocused] = useState(false);

    const buttonId = useId();
    const errorId = `${buttonId}-error`;

    // Update the current selected option if the preselectedIndex changes.
    useEffect(() => {
        setSelectedOption(placeholder && preselectedIndex === undefined ? -1 : preselectedIndex ? preselectedIndex : 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preselectedIndex]);

    let labelColor = 'text-gray-transparent';
    let focusedLabelColor = 'text-[var(--primary-color)]';
    let selectedItemBackgroundColor: string;
    let itemTextClassname: string;
    let listBackgroundColor: string;
    let selectableItemBackgroundColor: string;

    switch (style && typeof style !== 'string' ? style.style : style) {
        case BasicDropdownStyle.dropdown_on_white_bg:
            labelColor = 'text-darkBlue-transparent';
            focusedLabelColor = 'text-darkBlue';
            selectedItemBackgroundColor = classnames(
                'bg-gray-light active:opacity-80',
                style && typeof style !== 'string' && 'selectedItemClassName' in style
                    ? style.selectedItemClassName
                    : ''
            );
            listBackgroundColor = classnames(
                style && typeof style !== 'string' && 'listBgColorClass' in style
                    ? style.listBgColorClass
                    : 'bg-gray-light shadow-md'
            );
            selectableItemBackgroundColor = classnames(
                'bg-gray-light hover:bg-darkBlue/20 active:opacity-80',
                style && typeof style !== 'string' && 'itemBgColorClass' in style ? style.itemBgColorClass : ''
            );
            break;
        case BasicDropdownStyle.light:
            labelColor = 'text-darkBlue-transparent';
            focusedLabelColor = 'text-darkBlue';
            selectedItemBackgroundColor = classnames(
                'hover:bg-cyan/20 active:opacity-80',
                style && typeof style !== 'string' && 'selectedItemClassName' in style
                    ? style.selectedItemClassName
                    : ''
            );
            listBackgroundColor = classnames(
                style && typeof style !== 'string' && 'listBgColorClass' in style ? style.listBgColorClass : 'bg-white'
            );
            selectableItemBackgroundColor = classnames(
                'hover:bg-cyan/20 active:opacity-80',
                style && typeof style !== 'string' && 'itemBgColorClass' in style ? style.itemBgColorClass : ''
            );
            break;
        case BasicDropdownStyle.bg_white:
            selectedItemBackgroundColor = classnames(
                'bg-darkBlue/20 hover:bg-cyan hover:bg-darkBlue/20 active:opacity-80',
                style && typeof style !== 'string' && 'selectedItemClassName' in style
                    ? style.selectedItemClassName
                    : ''
            );
            listBackgroundColor = classnames(
                style && typeof style !== 'string' && 'listBgColorClass' in style ? style.listBgColorClass : 'bg-white'
            );
            itemTextClassname = 'text-darkBlue text-body-14 font-medium';
            selectableItemBackgroundColor = classnames(
                'hover:bg-opacity-50 active:opacity-80',
                style && typeof style !== 'string' && 'itemBgColorClass' in style
                    ? style.itemBgColorClass
                    : 'hover:bg-gray'
            );
            break;
        default:
            selectedItemBackgroundColor = classnames(
                'bg-white/20 hover:bg-cyan hover:bg-white/20 active:opacity-80',
                style && typeof style !== 'string' && 'selectedItemClassName' in style
                    ? style.selectedItemClassName
                    : ''
            );
            listBackgroundColor = classnames(
                style && typeof style !== 'string' && 'listBgColorClass' in style ? style.listBgColorClass : 'bg-white'
            );
            itemTextClassname = 'text-darkBlue text-body-14 font-medium';
            selectableItemBackgroundColor = classnames(
                'hover:bg-opacity-20 active:opacity-80',
                style && typeof style !== 'string' && 'itemBgColorClass' in style
                    ? style.itemBgColorClass
                    : 'bg-white/20 hover:bg-cyan'
            );
    }

    function enterFocus() {
        if (disabled !== true) {
            setFocused(true);
        }
    }

    function exitFocus() {
        setFocused(false);
    }

    function onChangeInternal(index: number) {
        const option = options[index];
        if (onChange) {
            const apply = onChange(option, index);
            if (apply !== false) {
                setSelectedOption(index);
            }
        } else {
            setSelectedOption(index);
        }
    }

    const useAnchor = isDropdownAbsolutePositioned !== false;
    const anchorPosition = openingDirection === 'up' ? 'top start' : 'bottom start';

    function renderOptions() {
        return options?.map((it, index) => (
            <ListboxOption
                key={`dropdownItem#${index}`}
                value={index}
                disabled={it.isSelectable === false}
                className={classnames(
                    // Fills the panel rather than hugging its own text, so hover and selection
                    // highlight the whole row and short options are not narrower than long ones.
                    'w-full active:opacity-80',
                    {
                        // Minimum (not fixed) row height: keeps the compact ~36px rhythm for
                        // single-line options, while a label too long for the panel width grows
                        // its own row instead of spilling over the neighbouring ones.
                        'min-h-36 px-12 py-6 flex items-center break-words': !React.isValidElement(it.label),
                        'cursor-pointer': it.isSelectable !== false
                    },
                    it.isSelectable !== false && selectableItemBackgroundColor,
                    selectableItemBackgroundColor,
                    itemClassName,
                    itemTextClassname
                )}>
                {it.label ?? '-'}
            </ListboxOption>
        ));
    }

    return (
        <div
            className={classnames(className, 'w-full flex flex-col')}
            onFocus={enterFocus}
            onBlur={exitFocus}>
            {label && (
                <InputLabel
                    label={label}
                    htmlFor={buttonId}
                    labelStyle={classnames(
                        { 'opacity-100': focused },
                        { [focusedLabelColor]: focused },
                        { [labelColor]: !focused }
                    )}
                    requiredLabel={props.required}
                />
            )}
            <Listbox
                as="div"
                value={selectedOption}
                onChange={onChangeInternal}
                disabled={disabled}>
                {({ open }) => (
                    <>
                        <CloseObserver
                            open={open}
                            onClose={onClose}
                        />
                        <ListboxButton
                            id={buttonId}
                            aria-invalid={!!error}
                            aria-describedby={error ? errorId : undefined}
                            className={classnames(
                                'flex flex-row items-center justify-between select-none space-x-12',
                                'transition-colors transition-opacity duration-100',
                                'active:opacity-80',
                                'rounded-4',
                                buttonClassName,
                                selectedItemBackgroundColor,
                                {
                                    'h-48': height !== 'custom',
                                    'h-full': height === 'custom',
                                    'px-12 py-6': !(
                                        options?.[selectedOption]?.label &&
                                        React.isValidElement(options[selectedOption].label)
                                    ),
                                    'opacity-50 cursor-not-allowed': disabled
                                }
                            )}>
                            {selectedOption === -1 ? (
                                <span>{placeholder}</span>
                            ) : (
                                <span>
                                    {options?.[selectedOption]?.isSelectable !== false &&
                                        (options?.[selectedOption]?.label ?? '-')}
                                </span>
                            )}
                            {customIcon || (
                                <div>
                                    <IconDown16
                                        className={classnames('transition-all duration-100 flex-none', {
                                            'mr-12':
                                                options?.[selectedOption]?.label &&
                                                React.isValidElement(options[selectedOption].label),
                                            'rotate-180': open
                                        })}
                                    />
                                </div>
                            )}
                        </ListboxButton>
                        {useAnchor ? (
                            <ListboxOptions
                                anchor={anchorPosition}
                                className={classnames(
                                    // `z-modal-view-overlay` (9100), not the previous arbitrary
                                    // z-[900]: headlessui portals this list to document.body, and
                                    // anything opened from inside a full-screen surface at
                                    // `z-modal-view` (9000) would otherwise render behind it — the
                                    // dropdown opens correctly and is simply invisible, which reads
                                    // as "the control does nothing".
                                    // `min-w` rather than a fixed `w`: the panel is at least as wide
                                    // as its button, but a label longer than a narrow button may
                                    // grow it — up to 480px, beyond which lines wrap instead of the
                                    // panel taking over the screen. (A button wider than 480px still
                                    // wins, since min-width beats max-width.)
                                    // The `!` is load-bearing: `anchor` makes Headless UI write
                                    // `max-width: <space available>px` INLINE, which a normal class
                                    // cannot outrank — only an important one can.
                                    'min-w-[var(--button-width)] !max-w-[480px] rounded-4',
                                    // No overflow or height of our own here. `anchor` also makes
                                    // Headless UI set `overflow: auto` and
                                    // `max-height: min(var(--anchor-max-height, 100vh), <space below
                                    // the button>)` inline, so THIS element is already the scroll
                                    // container. A second, fixed-height scroll box nested inside it
                                    // simply reached its limit first and scrolled instead — which is
                                    // how lists with ample room to open in full still ended up with
                                    // a scrollbar. `--anchor-max-height` is the supported way to cap
                                    // the panel, and unlike a `max-h` class it still yields to the
                                    // viewport when there is less room than the cap.
                                    '[--anchor-max-height:320px]',
                                    DROPDOWN_SCROLLBAR_CLASSNAME,
                                    'z-modal-view-overlay',
                                    'text-darkBlue',
                                    listBackgroundColor
                                )}>
                                {renderOptions()}
                                {paginationRef && <div ref={paginationRef} />}
                            </ListboxOptions>
                        ) : (
                            <ListboxOptions
                                className={classnames(
                                    'w-full rounded-4',
                                    // The opt-out (`isDropdownAbsolutePositioned={false}`) renders
                                    // in flow with no `anchor`, so Headless UI contributes no inline
                                    // sizing and this panel has to bound itself. Explicit axes,
                                    // never the `overflow-auto` shorthand: the list only ever
                                    // scrolls vertically, and a horizontal scrollbar would mean a
                                    // row wider than the panel, which `w-full` on each row (see
                                    // `renderOptions`) already rules out.
                                    'max-h-[320px] overflow-y-auto overflow-x-hidden',
                                    DROPDOWN_SCROLLBAR_CLASSNAME,
                                    'text-darkBlue',
                                    listBackgroundColor
                                )}>
                                {renderOptions()}
                                {paginationRef && <div ref={paginationRef} />}
                            </ListboxOptions>
                        )}
                    </>
                )}
            </Listbox>
            {error && (
                <ErrorLabel
                    error={error}
                    id={errorId}
                />
            )}
        </div>
    );
}

export default BasicDropdown;
